import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toSafeMarkdownHtml } from '@/core/utils/toSafeMarkdownHtml';
import { getDossierUrl } from '@/core/utils/getDossierUrl';
import { Post, PostTag } from '@/core/services/interfaces/PostInterfaces';
import { CardPostsLocale } from '@/features/publication-wall/constants/labels';
import { FullPostModal } from '../FullPostModal/FullPostModal';
import { TagAddModal } from '../TagAddModal/TagAddModal';
import {
  BdsIcon,
  BdsAvatar,
  BdsButton,
  BdsChipTag,
  BdsTooltip,
} from '@/core/constants/webComponents';
import { PostCardHashtags } from './PostCardHashtags';
import { PostStats } from './PostStats';
import { PostFooterMeta } from './PostFooterMeta';
import { PostCardAdvancedOptions } from './components/PostCardAdvancedOptions';
import { CARD_POST_EVENTS } from '@/features/publication-wall/constants/events';
import { sanitizeDisplayText } from '@/core/utils/sanitizeDisplayText';
import { useValidatedThumbnail } from '@/core/hooks/useValidatedThumbnail';
import { useTruncatedTooltip } from '@/core/hooks/useTruncatedTooltip';
import { PlatformIconLink } from '@/core/components/PlatformIconLink/PlatformIconLink';
import { getPostLanguageDisplayName } from '@/core/utils/postLanguage';
import './PostCard.scss';
import { useChannelIcon } from '@/core/hooks/useChannelIcon';
import { ChannelIconData } from '@/core/services/ChannelsService';
import { MediaRenderer } from '@/core/components/MediaRenderer/MediaRenderer';
import { resolveImageUrl, resolveVideoUrl, resolveVideoMimeType, resolveEmbedUrl, resolveAudioUrl } from '@/core/utils/resolvePostMediaUrl';
import { parseImageText } from '@/core/utils/parseImageText';
import { isStaticInteraction } from '@/core/utils/isStaticInteraction';
import {
  POLARITY_TO_NORMALIZED_SENTIMENT,
  GENDER_LABEL_TO_TYPE,
  SAC_TYPE_TO_ICON,
  SAC_TYPE_TO_TOOLTIP_KEY,
  type NormalizedSentiment,
  type Gender,
} from '@/core/constants/domainMappings';

const MAX_VISIBLE_TAGS = 3;

type SentimentType = NormalizedSentiment;
type ClassifyType = Gender;
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface PostCardProps {
  post: Post;
  locale: CardPostsLocale;
  channels?: Record<string, ChannelIconData>;
  existingTags?: string[];
}

interface MediaWarningProps {
  title?: string;
  description: string;
}

const MediaWarning: React.FC<MediaWarningProps> = ({ title, description }) => (
  <div className={`post-card__media-warning${title ? '' : ' post-card__media-warning--inbox-share'}`}>
    <span className="post-card__media-warning-icon" aria-hidden="true">
      <BdsIcon theme="outline" name="warning" size="medium" />
    </span>
    <div className="post-card__media-warning-content">
      {title && <strong className="post-card__media-warning-title">{title}</strong>}
      <p className="post-card__media-warning-text">{description}</p>
    </div>
  </div>
);

function parseChecked(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'sim'].includes(normalized)) return true;
    if (['false', '0', 'no', 'nao', 'não', ''].includes(normalized)) return false;
  }
  return Boolean(value);
}

function parseSentiment(value: unknown): SentimentType | undefined {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  return POLARITY_TO_NORMALIZED_SENTIMENT[String(value)] as SentimentType | undefined;
}

function parseClassify(value: unknown): ClassifyType {
  const normalizedValue = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  return GENDER_LABEL_TO_TYPE[normalizedValue] ?? 'man';
}

function formatSocialNetworkName(value?: string): string {
  if (!value) return '';
  return value
    .trim()
    .replaceAll('_', ' ')
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function getPostChannel(post: Post): string {
  return post.channel ?? post.primary_channel ?? '';
}

function getPlatform(post: Post): string {
  const channelToPlatform = post.primary_channel ?? post.channel ?? '';
  return channelToPlatform.toLowerCase();
}

function hasInitialMedia(post: Post, isBlocked: boolean): boolean {
  if (isBlocked) return false;

  const vid = resolveVideoUrl(post);
  const isTwitterVid = Boolean(vid && /video\.twimg\.com/.test(vid));
  const hasDisplayableVideo = Boolean(vid) && !isTwitterVid;

  return (
    Boolean(resolveEmbedUrl(post)) ||
    hasDisplayableVideo ||
    Boolean(resolveImageUrl(post)) ||
    Boolean(resolveAudioUrl(post))
  );
}

function getSacIconData(post: Post): { iconName: string; tooltipKey: string } | null {
  const sacType = (post.sac_type ?? '').trim().toLowerCase();
  const channelLabel = (post.channel ?? '').trim().toLowerCase();

  const mappedIcon = SAC_TYPE_TO_ICON[sacType];
  if (mappedIcon) {
    return {
      iconName: mappedIcon,
      tooltipKey: SAC_TYPE_TO_TOOLTIP_KEY[sacType] ?? 'cardPosts.sacTooltipComment',
    };
  }

  if (channelLabel.startsWith('comentarios do') || channelLabel.startsWith('comentários do')) {
    return {
      iconName: 'builder-text-message',
      tooltipKey: 'cardPosts.sacTooltipComment',
    };
  }

  return null;
}

function renderPostTypeBadges(
  t: (key: string) => string,
  isDarkPost: boolean,
  isCrossPost: boolean,
  isDynamicPost: boolean,
): React.ReactElement {
  return (
    <div className="post-card__badges">
      {isDarkPost && <span className="post-card__badge post-card__badge--dark">{t('cardPosts.darkPost')}</span>}
      {isCrossPost && <span className="post-card__badge post-card__badge--cross">{t('cardPosts.crossPost')}</span>}
      {isDynamicPost && <span className="post-card__badge post-card__badge--dynamic">{t('cardPosts.dynamicAd')}</span>}
    </div>
  );
}

function renderSacIcon(post: Post, t: (key: string) => string): React.ReactElement | null {
  const sacIconData = getSacIconData(post);
  if (!sacIconData) return null;

  return (
    <BdsTooltip position="top-right" tooltip-text={t(sacIconData.tooltipKey)}>
      <div className="post-card__sac-icon-wrapper">
        <BdsIcon theme="outline" name={sacIconData.iconName} size="medium" />
      </div>
    </BdsTooltip>
  );
}

function renderHotPostIcon(isHotPost: boolean, t: (key: string) => string): React.ReactElement | null {
  if (!isHotPost) return null;

  return (
    <BdsTooltip position="top-center" tooltip-text={t('cardPosts.hotPostTooltip')}>
      <span className="post-card__hot-icon">
        <BdsIcon name="fire" theme="outline" size="medium" color="#000000" />
      </span>
    </BdsTooltip>
  );
}



interface AuthorInfoProps {
  dossierURL: string;
  handleDossierClick: () => void;
  name: string;
  t: TranslateFn;
  verified?: boolean;
}

const AuthorInfo: React.FC<AuthorInfoProps> = ({
  dossierURL,
  handleDossierClick,
  name,
  t,
  verified,
}) => {
  const authorName = dossierURL ? (
    <button
      className="post-card__author-name post-card__author-name--clickable"
      onClick={handleDossierClick}
      aria-label={t('cardPosts.view')}
      title={t('cardPosts.view')}
    >
      {name}
    </button>
  ) : (
    <p className="post-card__author-name">
      {name}
    </p>
  );

  return (
    <div className="post-card__author-wrapper">
      {authorName}
      {verified && (
        <BdsTooltip position="top-center" tooltip-text={t('cardPosts.verifiedTooltipInfo')}>
          <span className="post-card__verified-icon">
            <BdsIcon name="checkball" theme="solid" size="small" />
          </span>
        </BdsTooltip>
      )}
    </div>
  );
};

function applyStatIfChanged<T>(value: T | undefined, setter: React.Dispatch<React.SetStateAction<T>>): void {
  if (value !== undefined) setter((prev) => (value === prev ? prev : value));
}

const usePostState = (post: Post) => {
  const [showModal, setShowModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);
  const [tags, setTags] = useState<PostTag[]>(
    (post.tags ?? []).map((label) => ({ label })),
  );
  const [initialChecked, setInitialChecked] = useState<boolean>(
    parseChecked(post.favorite ?? post.checked)
  );
  const [initialClassify, setInitialClassify] = useState<ClassifyType>(
    parseClassify(post.gender)
  );
  const [initialSentiment, setInitialSentiment] = useState<SentimentType | undefined>(
    parseSentiment(post.polarity)
  );
  const [metricsUpdatedAt, setMetricsUpdatedAt] = useState<string>(
    post.metrics_updated_at ?? ''
  );
  const [likes, setLikes] = useState<number | null>(post.likes ?? null);
  const [shares, setShares] = useState<number | null>(post.shares ?? null);
  const [comments, setComments] = useState<number | null>(post.comments ?? null);
  const [plays, setPlays] = useState<number | null>(post.videoplays ?? null);
  const [followers, setFollowers] = useState<number | null>(post.followers ?? null);
  const [isDarkPost, setIsDarkPost] = useState(post.isDarkPost ?? false);
  const [isDynamicPost, setIsDynamicPost] = useState(post.isDynamicPost ?? false);
  const [isCrossPost, setIsCrossPost] = useState(post.isCrossPost ?? false);
  const [isHotPost, setIsHotPost] = useState(post.isHotPost ?? false);

  const dossierURL = useMemo(() => getDossierUrl(post.userData), [post.userData]);

  useEffect(() => {
    const handleInitState = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      if (detail.postId !== post.pid) return;
      if (detail.metricsUpdatedAt !== undefined) setMetricsUpdatedAt(detail.metricsUpdatedAt);
      if (detail.polarity !== undefined) setInitialSentiment(parseSentiment(detail.polarity));
      if (detail.gender) setInitialClassify(parseClassify(detail.gender));
      if (detail.checked !== undefined) setInitialChecked(parseChecked(detail.checked));
      if (Array.isArray(detail.tags)) setTags(detail.tags.map((label: string) => ({ label })));
      if (detail.isDarkPost) setIsDarkPost(true);
      if (detail.isDynamicPost) setIsDynamicPost(true);
      if (detail.isCrossPost) setIsCrossPost(true);
      if (detail.isHotPost) setIsHotPost(true);
    };

    const handleRemove = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      if (detail.postId === post.pid) setIsRemoved(true);
    };

    const handleDetect = (setter: (v: boolean) => void) => (e: Event) => {
      if ((e as CustomEvent).detail?.postId === post.pid) setter(true);
    };

    const handleDark = handleDetect(setIsDarkPost);
    const handleDynamic = handleDetect(setIsDynamicPost);
    const handleCross = handleDetect(setIsCrossPost);
    const handleHot = handleDetect(setIsHotPost);

    const handleRefreshInteractions = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const postId = post.post_id ?? post.pid;
      if (detail.post?.pid !== postId && detail.post?.sac_call_id !== post.sac_call_id) return;
      const updatedPost: Partial<Post> = (e as CustomEvent).detail?.post ?? {};
      if (!updatedPost || updatedPost.pid !== post.pid) return;
      if (updatedPost.sac_call_id && post.sac_call_id && updatedPost.sac_call_id !== post.sac_call_id) return;
      applyStatIfChanged<number | null>(updatedPost.likes, setLikes);
      applyStatIfChanged<number | null>(updatedPost.shares, setShares);
      applyStatIfChanged<number | null>(updatedPost.comments, setComments);
      applyStatIfChanged<number | null>(updatedPost.videoplays, setPlays);
      applyStatIfChanged<number | null>(updatedPost.followers, setFollowers);
      applyStatIfChanged(updatedPost.metrics_updated_at, setMetricsUpdatedAt);
    };

    document.addEventListener(CARD_POST_EVENTS.REFRESH_POST_INTERACTIONS, handleRefreshInteractions);
    document.addEventListener(CARD_POST_EVENTS.INIT_POST_STATE, handleInitState);
    document.addEventListener(CARD_POST_EVENTS.REMOVE_POST, handleRemove);
    document.addEventListener(CARD_POST_EVENTS.DARK_POST_DETECTED, handleDark);
    document.addEventListener(CARD_POST_EVENTS.DYNAMIC_AD_DETECTED, handleDynamic);
    document.addEventListener(CARD_POST_EVENTS.CROSS_POST_DETECTED, handleCross);
    document.addEventListener(CARD_POST_EVENTS.HOT_POST_DETECTED, handleHot);
    document.dispatchEvent(
      new CustomEvent(CARD_POST_EVENTS.CARD_READY, {
        detail: { postId: post.pid },
        bubbles: true,
        composed: true,
      }),
    );

    return () => {
      document.removeEventListener(CARD_POST_EVENTS.REFRESH_POST_INTERACTIONS, handleRefreshInteractions);
      document.removeEventListener(CARD_POST_EVENTS.INIT_POST_STATE, handleInitState);
      document.removeEventListener(CARD_POST_EVENTS.REMOVE_POST, handleRemove);
      document.removeEventListener(CARD_POST_EVENTS.DARK_POST_DETECTED, handleDark);
      document.removeEventListener(CARD_POST_EVENTS.DYNAMIC_AD_DETECTED, handleDynamic);
      document.removeEventListener(CARD_POST_EVENTS.CROSS_POST_DETECTED, handleCross);
      document.removeEventListener(CARD_POST_EVENTS.HOT_POST_DETECTED, handleHot);
    };
  }, [post.pid, post.post_id, post.sac_call_id]);

  return {
    states: { showModal, showTagModal, isRemoved, tags, initialChecked, initialClassify, initialSentiment, metricsUpdatedAt, likes, shares, comments, plays, followers, isDarkPost, isDynamicPost, isCrossPost, isHotPost, dossierURL },
    setters: { setShowModal, setShowTagModal, setTags }
  };
};

interface TagChipButtonProps {
  label: string;
  index: number;
  onClick: (index: number) => void;
}

const TagChipButton: React.FC<TagChipButtonProps> = ({ label, index, onClick }) => {
  const { tooltip, showTooltip, hideTooltip } = useTruncatedTooltip({
    targetSelector: '.post-card__tag-chip-text',
  });

  return (
    <button
      type="button"
      className="post-card__tag-chip-btn"
      title={label}
      aria-label={label}
      onClick={() => onClick(index)}
      onMouseEnter={(event) => showTooltip(event, label)}
      onMouseLeave={hideTooltip}
      onFocus={(event) => showTooltip(event, label)}
      onBlur={hideTooltip}
    >
      <BdsChipTag color="outline">
        <span className="post-card__tag-chip-text">{label}</span>
      </BdsChipTag>
      {tooltip && (
        <span className="post-card__tag-chip-tooltip" style={{ top: tooltip.top, left: tooltip.left }}>
          {tooltip.text}
        </span>
      )}
    </button>
  );
};

export const PostCard: React.FC<PostCardProps> = ({ post, locale, channels = {}, existingTags = [] }) => {
  const isUnsupportedMedia = post.features?.includes('UNSUPPORTED_MEDIA') ?? false;
  const isInboxShare = post.features?.includes('INBOX_SHARE') ?? false;
  const sharedUrl = post.text?.trim() || post.attachments?.[0]?.url || '';
  const { t } = useTranslation();
  const { states, setters } = usePostState(post);
  const {
    showModal, showTagModal, isRemoved, tags, initialChecked, initialClassify,
    initialSentiment, metricsUpdatedAt, likes, shares, comments, plays, followers,
    isDarkPost, isDynamicPost, isCrossPost, isHotPost, dossierURL
  } = states;
  const { setShowModal, setShowTagModal, setTags } = setters;

  const platform = getPlatform(post);
  const channel = getPostChannel(post);
  const socialNetworkName = formatSocialNetworkName(post.channel ?? post.primary_channel ?? platform);
  const publicationUrl = post.post_url ?? '';
  const channelData = useChannelIcon(platform, channels[platform]);
  const avatarThumbnail = useValidatedThumbnail(post.post_user_image_url);
  const [hasMedia, setHasMedia] = useState<boolean>(() => hasInitialMedia(post, isUnsupportedMedia || isInboxShare));

  const handleTagAdded = (tagLabel: string) => {
    setTags((prev) => [...prev, { label: tagLabel }]);
    document.dispatchEvent(
      new CustomEvent(CARD_POST_EVENTS.TAG_ADDED, {
        detail: { postId: post.pid, tag: tagLabel, channel },
        bubbles: true,
        composed: true,
      }),
    );
  };

  const handleTagRemoved = (tagLabel: string) => {
    setTags((prev) => prev.filter((t) => t.label !== tagLabel));
    document.dispatchEvent(
      new CustomEvent(CARD_POST_EVENTS.TAG_REMOVED, {
        detail: { postId: post.pid, tag: tagLabel, channel },
        bubbles: true,
        composed: true,
      }),
    );
  };

  const dispatchTagRowClick = (
    action: 'tag' | 'more-items' | 'add-tag',
    value: string,
    index?: number,
  ) => {
    document.dispatchEvent(
      new CustomEvent(CARD_POST_EVENTS.TAG_ROW_CLICK, {
        detail: { postId: post.pid, action, value, index, hiddenTagsCount },
        bubbles: true,
        composed: true,
      }),
    );
  };

  const handleReadFullPostClick = () => {
    document.dispatchEvent(
      new CustomEvent(CARD_POST_EVENTS.READ_FULL_POST_CLICK, {
        detail: { postId: post.pid, action: 'open-modal', value: t('cardPosts.readFullPost') },
        bubbles: true,
        composed: true,
      }),
    );
    setShowModal(true);
  };

  const handleDossierClick = () => {
    if (dossierURL) {
      window.open(dossierURL, '_blank', 'noopener,noreferrer');
    }
  };

  const handlePlatformIconClick = () => {
    if (!publicationUrl) return;
    document.dispatchEvent(
      new CustomEvent(CARD_POST_EVENTS.FULL_POST_MODAL_CLICK, {
        detail: { postId: post._id ?? post.pid, action: 'open-publication-link', value: publicationUrl },
        bubbles: true,
        composed: true,
      }),
    );
  };

  const imageUrl = isUnsupportedMedia || isInboxShare ? '' : resolveImageUrl(post);
  const videoUrl = isUnsupportedMedia || isInboxShare ? '' : resolveVideoUrl(post);
  const videoMimeType = isUnsupportedMedia || isInboxShare ? undefined : resolveVideoMimeType(post);
  const videoPreload = !imageUrl && videoUrl ? 'metadata' : 'none';
  const embedUrl = isUnsupportedMedia || isInboxShare ? '' : resolveEmbedUrl(post);
  const audioUrl = isUnsupportedMedia || isInboxShare ? '' : resolveAudioUrl(post);
  const hasTitle = Boolean(post.title?.trim());
  const renderedTitleHtml = hasTitle ? toSafeMarkdownHtml(post.title!.trim()) : '';
  const renderedTextHtml = toSafeMarkdownHtml(post.text ?? '');
  const imageTextHtml = parseImageText(post.computer_vision, t('cardPosts.computerVisionHeader'));
  const publicationTooltip = t('cardPosts.viewPublicationTooltip', { socialNetworkName });
  const postLanguage = getPostLanguageDisplayName(post.lang, t);
  const isEncryptedConversation = (post.features ?? []).some(
    (feature) => feature.toUpperCase() === 'ENCRYPTED',
  );

  const visibleTags = useMemo(() => tags.slice(0, MAX_VISIBLE_TAGS), [tags]);
  const hiddenTagsCount = Math.max(0, tags.length - MAX_VISIBLE_TAGS);

  const renderUnencryptedBody = () => {
    if (isUnsupportedMedia) {
      return (
        <div className="post-card__warning-wrapper">
          <MediaWarning
            title={t('cardPosts.unsupportedMediaTitle')}
            description={t('cardPosts.unsupportedMediaDesc')}
          />
        </div>
      );
    }

    if (isInboxShare) {
      return (
        <div className="post-card__warning-wrapper">
          {sharedUrl && (
            <a className="post-card__inbox-share-link" href={sharedUrl} target="_blank" rel="noopener noreferrer">
              {sharedUrl}
            </a>
          )}
          <MediaWarning description={t('cardPosts.inboxShareDesc')} />
        </div>
      );
    }

    return (
      <>
        {hasTitle && (
          <p className="post-card__title" dangerouslySetInnerHTML={{ __html: renderedTitleHtml }} />
        )}
        <p className="post-card__text" dangerouslySetInnerHTML={{ __html: renderedTextHtml }} />
        {imageTextHtml && (
          <div className="post-card__image-text" dangerouslySetInnerHTML={{ __html: imageTextHtml }} />
        )}
      </>
    );
  };

  if (isRemoved) return null;

  return (
    <>
      <article className={`post-card${hasMedia ? ' post-card--with-image' : ''}${isEncryptedConversation ? ' post-card--encrypted' : ''}${isUnsupportedMedia || isInboxShare ? ' post-card--media-warning' : ''}`}>
        {/* ── Post-type badges ── */}
        {renderPostTypeBadges(t, isDarkPost, isCrossPost, isDynamicPost)}

        {/* ── Image ── */}
        <MediaRenderer
          videoUrl={videoUrl}
          videoMimeType={videoMimeType}
          embedUrl={embedUrl}
          imageUrl={imageUrl}
          audioUrl={audioUrl}
          alt={post.name ?? ''}
          wrapperClassName="post-card__image-wrapper"
          mediaClassName={hasMedia ? 'post-card__image' : 'post-card__image--hidden'}
          audioClassName="post-card__image post-card__audio"
          videoPreload={videoPreload}
          videoPoster={imageUrl}
          onHasMediaChange={setHasMedia}
        />


        {/* ── Header ── */}
        <div className="post-card__header-section">
          <PlatformIconLink
            publicationUrl={publicationUrl}
            tooltipText={publicationTooltip}
            channelIconUrl={channelData?.icons?.default}
            platform={platform}
            wrapperClassName="post-card__platform-icon-wrapper"
            linkClassName="post-card__platform-icon post-card__platform-icon--clickable"
            buttonClassName="post-card__platform-icon"
            tooltipPosition="top-left"
            onNavigate={handlePlatformIconClick}
          />
          {renderSacIcon(post, t)}
          <div className="post-card__header-content">
            <BdsAvatar
              class={`post-card__avatar-wrapper${dossierURL ? ' post-card__avatar-wrapper--clickable' : ''}`}
              size="extra-small"
              name={sanitizeDisplayText(post.name) ?? post.name}
              thumbnail={avatarThumbnail}
              onClick={handleDossierClick}
            />
            <AuthorInfo
              dossierURL={dossierURL}
              handleDossierClick={handleDossierClick}
              name={post.name ?? ''}
              t={t}
              verified={post.verified}
            />
            <div className="post-card__meta">
              <span className="post-card__date">{post.posted_at}</span>
              {postLanguage && (
                <span className="post-card__language">
                  {t('cardPosts.language')} {postLanguage}
                </span>
              )}
            </div>
          </div>
          <div className="post-card__header-indicators">
            {post.replied && (
              <BdsTooltip position="top-center" tooltip-text={t('cardPosts.repliedTooltip')}>
                <span className="post-card__replied-icon">
                  <BdsIcon theme="outline" name="message-read" size="medium" />
                </span>
              </BdsTooltip>
            )}
            {renderHotPostIcon(isHotPost, t)}
          </div>
        </div>

        {/* ── Text (grows to fill available space) ── */}
        <div className="post-card__body">
          {isEncryptedConversation ? (
            <div className="post-card__encrypted-warning" role="note" aria-live="polite">
              <div className="post-card__encrypted-warning-content">
                <span className="post-card__encrypted-warning-icon" aria-hidden="true">
                  <BdsIcon theme="outline" name="warning" size="medium" />
                </span>
                <p className="post-card__encrypted-warning-text">
                  {t('cardPosts.encryptedWarningStart')}
                  <strong>{t('cardPosts.encryptedWarningConnections')}</strong>
                  {t('cardPosts.encryptedWarningEnd')}
                </p>
              </div>
            </div>
          ) : (
            renderUnencryptedBody()
          )}
        </div>

        {/* ── Hashtags + read-more (always at the same position) ── */}
        <PostCardHashtags hashtags={post.hashtags ?? []} postId={post.pid} />
        <span className="post-card__read-more-wrapper">
          <BdsButton
            class="post-card__read-more"
            variant="outline"
            icon-right="add"
            size="short"
            color="content"
            title={t('cardPosts.readFullPost')}
            onClick={handleReadFullPostClick}
          >
            {t('cardPosts.readFullPost')}
          </BdsButton>
        </span>

        {/* ── Tags row ── */}
        <div className="post-card__tags-row">
          <span className="post-card__tags-label" title={t('cardPosts.tags')}>
            {t('cardPosts.tags')}
          </span>
          {visibleTags.map((tag, index) => (
            <TagChipButton
              key={`${tag.label}-${index}`}
              label={tag.label}
              index={index}
              onClick={(tagIndex) => dispatchTagRowClick('tag', tag.label, tagIndex)}
            />
          ))}
          {hiddenTagsCount > 0 && (
            <BdsTooltip position="top-center" tooltip-text={`${t('cardPosts.moreItems')} (+${hiddenTagsCount})`}>
              <button
                type="button"
                className="post-card__tag-chip-btn"
                title={`${t('cardPosts.moreItems')} (+${hiddenTagsCount})`}
                aria-label={`${t('cardPosts.moreItems')} (+${hiddenTagsCount})`}
                onClick={() => dispatchTagRowClick('more-items', t('cardPosts.moreItems'), MAX_VISIBLE_TAGS)}
              >
                <BdsChipTag color="outline">+{hiddenTagsCount}</BdsChipTag>
              </button>
            </BdsTooltip>
          )}
          <BdsTooltip position="top-center" tooltip-text={t('cardPosts.addTags')}>
            <button
              type="button"
              className="post-card__tag-add"
              title={t('cardPosts.addTags')}
              aria-label={t('cardPosts.addTags')}
              onClick={() => { dispatchTagRowClick('add-tag', t('cardPosts.addTags')); setShowTagModal(true); }}
            >
              <BdsIcon theme="outline" name="plus" size="small" />
            </button>
          </BdsTooltip>
        </div>

        <hr className="post-card__divider" />

        {/* ── Stats ── */}
        <PostStats
          platform={platform}
          likes={likes}
          shares={shares}
          comments={comments}
          plays={plays}
          followers={followers}
        />

        {/* ── Footer metadata ── */}
        <PostFooterMeta
          updatedAt={metricsUpdatedAt}
          groups={(post.names ?? []).map((name) => ({ name }))}
          themes={(post.themes ?? []).map((name) => ({ name }))}
          interest={(post.occupations ?? []).map((name) => ({ name }))}
          postId={post.pid ?? post._id}
          uid={post.uid}
          channel={channel}
          platform={platform}
          isInteraction={isStaticInteraction(post)}
          post_id={post.post_id}
          sac_call_id={post.sac_call_id}
        />

        <hr className="post-card__divider" />

        {/* ── Advanced options ── */}
        <PostCardAdvancedOptions
          channel={channel}
          uid={post.uid ?? ''}
          postUrl={publicationUrl}
          postPid={post.pid ?? ''}
          initialChecked={initialChecked}
          initialClassify={initialClassify}
          initialSentiment={initialSentiment}
        />
      </article>

      {showModal && <FullPostModal post={post} locale={locale} onClose={() => setShowModal(false)} />}
      {showTagModal && (
        <TagAddModal
          tags={tags}
          existingTags={existingTags}
          postId={post.pid}
          onClose={() => setShowTagModal(false)}
          onTagAdded={handleTagAdded}
          onTagRemoved={handleTagRemoved}
        />
      )}
    </>
  );
};
